import { Flex, Stack } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast, ToastType } from 'react-toastify';

import type { Billable, Campaign, Event, IStripe, TSource } from '@feathr/blackbox';
import {
  AlertV2 as Alert,
  Button,
  CardV2 as Card,
  EAlertV2Type as EAlertType,
  EmptyState,
  Label,
  Well,
} from '@feathr/components';
import PaymentMethodForm from '@feathr/extender/App/Settings/Billing/BillablePage/PaymentMethodForm';
import BillingSource from '@feathr/extender/components/BillingSource';
import { useLocalUrl } from '@feathr/extender/state';
import { currencyFormatter } from '@feathr/extender/utils';
import { cssVar, errorMessage, useToggle } from '@feathr/hooks';

import * as styles from './PaymentDetails.css';

interface IProps {
  billable?: Billable;
  event: Event;
  campaign: Campaign;
}

function PaymentDetails({ billable, event, campaign }: Readonly<IProps>): JSX.Element {
  const localUrl = useLocalUrl();
  const [adding, toggleAdding] = useToggle(false);
  const { t } = useTranslation();

  const billing = event.get('billing');
  const balance = billing?.balance || 0;
  const budget = campaign.get('exposure_settings').target_value || 0;
  const spend = campaign.get('stats_cache')?.spend || 0;

  function setSource(billable: Billable, source: TSource): void {
    const stripe = billable.get('stripe');
    billable.set({ stripe: { ...stripe, source, _token: source.id } });
  }

  function renderNoBillable(): JSX.Element {
    return (
      <Alert
        className={styles.marginBuster}
        title={
          <Trans t={t}>
            You will not be able to publish this campaign until you&nbsp;
            <Link target={'_blank'} to={localUrl('/settings/billing/configurations')}>
              set up a billing configuration
            </Link>
            &nbsp;and&nbsp;
            <Link target={'_blank'} to={localUrl(event.getItemUrl('/settings/billing/edit'))}>
              attach it to this project
            </Link>
            .
          </Trans>
        }
        type={EAlertType.danger}
      />
    );
  }

  function renderBillableWithSource(): JSX.Element {
    if (!billable) {
      throw new Error('billable must be defined');
    }
    return (
      <div>
        <Label>{t('Payment method')}</Label>
        <BillingSource
          billable={billable as Billable}
          key={billable.get('stripe').source!.id}
          source={billable.get('stripe').source!}
        />
      </div>
    );
  }

  function renderAddPaymentMethod(): JSX.Element {
    return (
      <Stack>
        <EmptyState label={t('Payment method has not been set')} theme={'neutral'}>
          <Button onClick={toggleAdding}>{t('Add payment method')}</Button>
        </EmptyState>
        <Alert
          className={styles.marginBuster}
          title={t(
            'Changing the payment method above will affect the billing configuration for the whole project.',
          )}
          type={EAlertType.info}
        />
      </Stack>
    );
  }

  function renderMediaCreditBalance(): JSX.Element {
    return (
      <Well className={styles.mediaCreditBalance} layout={'vertical'}>
        <Label
          className={styles.marginBuster}
          tooltip={t(
            'This number represents the amount of money you have already funded on this project (in USD).',
          )}
        >
          {t('Media Credit Balance')}
        </Label>
        <span className={styles.bigNumber} data-name={'media_credit_balance'}>
          {currencyFormatter.format(Math.max(balance, 0))}
        </span>
      </Well>
    );
  }

  function renderPaymentMethodForm(): JSX.Element {
    return (
      <PaymentMethodForm>
        {(onSave, element): JSX.Element => {
          async function handleSave(): Promise<void> {
            if (!billable || billable.isPending) {
              return;
            }

            try {
              const updatedSource = await onSave();
              setSource(billable, updatedSource);
              await billable.save();
              toggleAdding();
            } catch (error) {
              const message = errorMessage(
                error,
                t('An error occurred while trying to add a payment method.'),
              );
              toast(message, { type: ToastType.ERROR });
            }
          }

          return (
            <>
              <Card.Content>{element}</Card.Content>
              <Card.Actions>
                <Button onClick={toggleAdding} type={'naked'}>
                  {t('Cancel')}
                </Button>
                <Button onClick={handleSave}>{t('Add')}</Button>
              </Card.Actions>
            </>
          );
        }}
      </PaymentMethodForm>
    );
  }

  // Main render logic
  let mainContent: JSX.Element | null;
  if (!adding) {
    let paymentSection: JSX.Element | null;
    if (!billable) {
      paymentSection = renderNoBillable();
    } else if (!billable.isPending && billable.get('stripe', {} as IStripe).source) {
      paymentSection = renderBillableWithSource();
    } else if (budget - spend > balance) {
      paymentSection = renderAddPaymentMethod();
    } else {
      paymentSection = null;
    }

    mainContent = (
      <Card.Content addVerticalGap={true}>
        <Flex align={'center'} gap={cssVar('--spacing-4')} justify={'space-between'}>
          {paymentSection}
          {balance > 0 && renderMediaCreditBalance()}
        </Flex>
      </Card.Content>
    );
  } else if (!!billable && !billable.isPending) {
    mainContent = renderPaymentMethodForm();
  } else {
    mainContent = null;
  }

  return (
    <Card>
      <Card.Header title={'Payment Details'} />
      {mainContent}
    </Card>
  );
}

export default PaymentDetails;
